import { useState } from "react";
import { type Project, deleteProject } from "@/lib/projectStore";
import { useNavigate } from "react-router-dom";
import { Badge } from "@/components/ui/badge";
import { FileText, Clock, GitBranch, Users, Trash2 } from "lucide-react";

const statusStyles: Record<Project["status"], string> = {
  active: "bg-accent text-accent-foreground",
  draft: "bg-secondary text-secondary-foreground",
  archived: "bg-muted text-muted-foreground",
};

export default function ProjectCard({
  project,
  onDeleted,
}: {
  project: Project;
  onDeleted?: () => void;
}) {
  const navigate = useNavigate();
  const [confirming, setConfirming] = useState(false);
  const date = new Date(project.updatedAt).toLocaleDateString("en-US", {
    month: "short",
    day: "numeric",
  });

  const storyCount = project.userStories?.length || 0;
  const analysisCount = project.analyses?.length || 0;

  const handleDelete = async (e: React.MouseEvent) => {
    e.stopPropagation();
    if (!confirming) { setConfirming(true); return; }
    await deleteProject(project.id);
    onDeleted?.();
  };

  const handleCardClick = () => {
    if (confirming) { setConfirming(false); return; }
    navigate(`/studio/project/${project.id}`);
  };

  return (
    <button
      onClick={handleCardClick}
      className="group text-left w-full border border-border rounded-md p-5 hover:border-accent transition-colors bg-card animate-fade-in relative"
    >
      <div className="flex items-start justify-between mb-3">
        <div className="flex items-center gap-2">
          <FileText className="h-4 w-4 text-muted-foreground" />
          <h3 className="font-display font-semibold text-card-foreground group-hover:text-accent transition-colors">
            {project.name}
          </h3>
        </div>
        <div className="flex items-center gap-2">
          <Badge className={statusStyles[project.status]} variant="secondary">
            {project.status}
          </Badge>
          <span
            onClick={handleDelete}
            className={`p-1 rounded transition-colors ${
              confirming
                ? "text-destructive bg-destructive/10"
                : "text-muted-foreground/40 hover:text-destructive opacity-0 group-hover:opacity-100"
            }`}
            title={confirming ? "Click again to confirm delete" : "Delete project"}
          >
            <Trash2 className="h-3.5 w-3.5" />
          </span>
        </div>
      </div>
      {confirming && (
        <p className="text-xs text-destructive mb-2">Click trash again to confirm, or click elsewhere to cancel</p>
      )}
      {project.description && (
        <p className="text-sm text-muted-foreground line-clamp-2 mb-3 font-body">
          {project.description}
        </p>
      )}
      <div className="flex items-center gap-4 text-xs text-muted-foreground">
        <div className="flex items-center gap-1">
          <Clock className="h-3 w-3" />
          <span>{date}</span>
        </div>
        <div className="flex items-center gap-1">
          <Badge variant="outline" className="text-[10px] px-1.5 py-0">
            {project.methodology || "agile"}
          </Badge>
        </div>
        {storyCount > 0 && (
          <div className="flex items-center gap-1">
            <GitBranch className="h-3 w-3" />
            <span>{storyCount} stories</span>
          </div>
        )}
        {analysisCount > 0 && (
          <div className="flex items-center gap-1">
            <Users className="h-3 w-3" />
            <span>{analysisCount} analyses</span>
          </div>
        )}
      </div>
    </button>
  );
}
